import React, { useEffect } from "react";
import { Menu, Button, CircularProgress } from "@material-ui/core";
import { func, object } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadNotificationCount,
  getNotificationSettings,
} from "../../../redux/actions/settingsActions";
import EmptyState from "../../../components/EmptyState";

const Notifications = ({ anchorEl, handleClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { notifications, notificationSettings } = useSelector(({ settings }) => settings);

  useEffect(() => {
    getNotifications(dispatch);
    getUnreadNotificationCount(dispatch);
    getNotificationSettings(dispatch);
  }, [dispatch]);

  // Sprint 3: deep-link to the relevant feed card based on target_type + target_id
  const navigateToTarget = (notification) => {
    const { target_type, target_id } = notification;
    if (!target_type || !target_id) return;

    switch (target_type) {
      case "editorial":
      case "feedCard":
        // Navigate to the Workbrook tab with the specific card highlighted
        history.push(`/league?card=${target_id}`);
        break;
      case "userPost":
        // Navigate to the Community tab with the specific post highlighted
        history.push(`/league?tab=community&post=${target_id}`);
        break;
      case "job":
        // Navigate to the job detail page
        history.push(`/home/${target_id}`);
        break;
      case "leaderboard":
        // Navigate to the Workbrook tab (leaderboard is at the top)
        history.push(`/league`);
        break;
      default:
        // No deep-link target — stay on current page
        break;
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.is_read === 0 || notification.is_read === "0") {
      markNotificationAsRead(dispatch, notification.id).then(() => {
        getNotifications(dispatch);
      });
    }
    // Sprint 3: deep-link to the relevant feed content
    navigateToTarget(notification);
    if (handleClose) handleClose();
  };

  const handleMarkAllAsRead = () => {
    markAllNotificationsAsRead(dispatch);
  };

  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const unreadCount = safeNotifications.filter(
    (n) => n.is_read === 0 || n.is_read === "0"
  ).length;

  return (
    <Menu
      anchorEl={anchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
      aria-labelledby="notifications"
      PaperProps={{
        style: {
          width: "26rem",
          borderRadius: "14px",
          padding: 0,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerTitle}>Notifications</span>
          {unreadCount > 0 && (
            <span style={styles.badge}>{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button style={styles.markAllBtn} onClick={handleMarkAllAsRead}>
            ✓ Mark all read
          </button>
        )}
      </div>

      {/* ── Divider ── */}
      <div style={styles.divider} />

      {/* ── Body ── */}
      <div style={styles.body}>
        {notificationSettings.push_notification !== "1" ? (
          <div style={styles.emptyWrap}>
            <div style={styles.emptyIcon}>🔕</div>
            <span style={styles.emptyTitle}>In-App Notifications Disabled</span>
            <span style={styles.emptyMsg}>You have disabled in-app notifications in your settings.</span>
          </div>
        ) : safeNotifications.length === 0 ? (
          <div style={styles.emptyWrap}>
            <div style={styles.emptyIcon}>🔕</div>
            <span style={styles.emptyTitle}>All caught up!</span>
            <span style={styles.emptyMsg}>You have no notifications right now.</span>
          </div>
        ) : (
          <div style={styles.list}>
            {safeNotifications.map((notification, index) => {
              const isUnread =
                notification.is_read === 0 || notification.is_read === "0";
              return (
                <div
                  key={index}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    ...styles.item,
                    backgroundColor: isUnread ? "#F0F7FF" : "#FFFFFF",
                    borderLeft: `3px solid ${isUnread ? "#3a6ed4" : "transparent"}`,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = isUnread ? "#E3F2FD" : "#F8F9FA")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = isUnread ? "#F0F7FF" : "#FFFFFF")
                  }
                >
                  {/* Avatar */}
                  <img
                    width="40"
                    height="40"
                    style={styles.avatar}
                    src={notification.image_url}
                    alt="avatar"
                  />

                  {/* Text */}
                  <div style={styles.itemText}>
                    <div style={styles.itemRow}>
                      {/* <span
                        style={{
                          ...styles.itemCategory,
                          fontWeight: isUnread ? "700" : "600",
                        }}
                      >
                        {notification.category}
                      </span> */}
                      <span style={styles.itemTime}>
                        {new Date(notification.date_created).toLocaleString(
                          undefined,
                          { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
                        )}
                      </span>
                    </div>
                    <span
                      style={{
                        ...styles.itemMessage,
                        fontWeight: isUnread ? "500" : "400",
                        color: isUnread ? "#1A202C" : "#64748B",
                      }}
                    >
                      {notification.message}
                    </span>
                  </div>

                  {/* Unread dot */}
                  {isUnread && <span style={styles.unreadDot} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Menu>
  );
};

Notifications.propTypes = {
  anchorEl: object,
  handleClose: func.isRequired,
};

export default Notifications;

// ── Styles ──────────────────────────────────────────────────────────
const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 18px 14px",
    backgroundColor: "#fff",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerTitle: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#0F172A",
    letterSpacing: "-0.3px",
  },
  badge: {
    backgroundColor: "#FF5964",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "800",
    borderRadius: "10px",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  markAllBtn: {
    background: "#EBF6FD",
    border: "1px solid #1E93E2",
    borderRadius: "7px",
    padding: "5px 11px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#1E93E2",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  divider: {
    height: "1px",
    backgroundColor: "#F0F0F0",
  },
  body: {
    maxHeight: "420px",
    overflowY: "auto",
    backgroundColor: "#F5F7FB",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px 16px",
    cursor: "pointer",
    transition: "background-color 0.15s",
    position: "relative",
  },
  avatar: {
    borderRadius: "10px",
    objectFit: "cover",
    flexShrink: 0,
    border: "1px solid #E5E5E5",
  },
  itemText: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    minWidth: 0,
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  itemCategory: {
    fontSize: "13px",
    color: "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemTime: {
    fontSize: "11px",
    color: "#94A3B8",
    flexShrink: 0,
    fontWeight: "400",
  },
  itemMessage: {
    fontSize: "13px",
    lineHeight: "1.45",
    wordBreak: "break-word",
  },
  unreadDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#FF5964",
    flexShrink: 0,
    marginTop: "4px",
  },
  emptyWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    gap: "10px",
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "32px",
    backgroundColor: "#EBF6FD",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    marginBottom: "4px",
  },
  emptyTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#0F172A",
  },
  emptyMsg: {
    fontSize: "13px",
    color: "#94A3B8",
    textAlign: "center",
  },
};